import { type AiResponse, CreateAiReportReq, type File, FileIdList, Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  LoadingIcon,
  Textarea,
  toast,
} from '@kalos/ui';
import { CopyIcon, DownloadIcon, EyeOpenIcon, ImageIcon } from '@radix-ui/react-icons';
import { format, parseISO } from 'date-fns/esm';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ListRow } from '../../../../components/ListRow';
import { useAuth } from '../../../../context/AuthContext';
import { useBatchGetAiResponsesSystemQuery } from '../../../../hooks/react-query/useAiResponseReportQuery';
import {
  useMemoClassificationQuery,
  useMemoQuery,
  useMemoReportQuery,
} from '../../../../hooks/react-query/useMemoQuery';
import { useDownloadZipMutation } from '../../../../hooks/react-query/useS3Query';
import { useMobileDevice } from '../../../../hooks/useMedia';
import { OmniGallery } from '../../OmniGallery';
import { PhotoThumbnail, PhotoViewer } from '../../ServiceCall/components/Notes/PhotoGallery';
import { copyReportToClipboard } from '../reportClipboard';

interface MemoReportFormProps {
  memoRequest: Memo;
  aiResponse?: AiResponse; // Optional: if provided, shows the report and uses memoIds from it
  enabled?: boolean;
  onClose?: () => void;
  /** Phones: Build PDF and Download PDF for a saved report, shown under Copy report. */
  onBuildPdf?: () => void;
  onDownloadPdf?: () => void;
  /** The label of the button that calls `onClose` after a report is shown. */
  closeLabel?: string;
  onSuccess?: () => void;
}

type ReportType = 'department' | 'status' | 'job' | 'custom';

type WorkflowStep = 'form' | 'generating' | 'generated';

interface ReportFormValues {
  reportType: ReportType;
  customQuery: string;
}

/** Extract start/end calendar dates from the operator-prefixed dateRange tuple (e.g. ['>=', '2026-01-01', '<', '2026-04-08']). */
function extractPeriodFromDateRange(dateRange?: string[]): {
  start: string;
  end: string;
} {
  let start = '';
  let end = '';
  if (!dateRange || dateRange.length === 0) return { start, end };
  for (let i = 0; i < dateRange.length - 1; i++) {
    const op = dateRange[i];
    const val = dateRange[i + 1];
    if ((op === '>=' || op === '>') && val) {
      start = val.slice(0, 10);
    } else if ((op === '<' || op === '<=') && val) {
      end = val.slice(0, 10);
    }
  }
  // If start exists but end is open-ended, default to today
  if (start && !end) {
    end = format(new Date(), 'yyyy-MM-dd');
  }
  return { start, end };
}

export const MemoReportForm = ({
  memoRequest,
  aiResponse,
  enabled = true,
  onClose,
  closeLabel = 'Close',
  onBuildPdf,
  onDownloadPdf,
  onSuccess,
}: MemoReportFormProps) => {
  const { user } = useAuth();
  // If aiResponse is provided, start in 'generated' state to show the report
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>(aiResponse ? 'generated' : 'form');
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [isPhotoGridOpen, setIsPhotoGridOpen] = useState(false);
  const [openPhotoIndex, setOpenPhotoIndex] = useState<number | null>(null);
  const isMobileDevice = useMobileDevice();
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [fileToMemoMap, setFileToMemoMap] = useState<Map<number, Memo>>(new Map());
  const [reportRequest, setReportRequest] = useState<CreateAiReportReq | null>(null);
  const form = useForm<ReportFormValues>({
    defaultValues: {
      reportType: 'job',
      customQuery: '',
    },
  });

  const reportType = form.watch('reportType');

  const reportTypes = [
    {
      id: 'department' as const,
      title: 'Department Summary',
      description: 'Breakdown by department with completion rates.',
      comingSoon: true,
    },
    {
      id: 'status' as const,
      title: 'Status Summary',
      description: 'Analyze notes by status (Pending, In Progress, Completed).',
      comingSoon: true,
    },
    {
      id: 'job' as const,
      title: 'Job Summary',
      description: 'Breakdown by job number with activity details.',
      comingSoon: false,
    },
    {
      id: 'custom' as const,
      title: 'Custom Query',
      description: 'Ask a specific question about your notes.',
      comingSoon: true,
    },
  ];

  const availableReportTypes = reportTypes.filter((type) => !type.comingSoon);

  const handleReportTypeClick = (type: ReportType) => {
    const reportTypeConfig = reportTypes.find((rt) => rt.id === type);
    if (!reportTypeConfig?.comingSoon) {
      form.setValue('reportType', type);
    }
  };

  // Check if a filter is applied (date range, job/eventId, project scope, department, or idList)
  const hasFilterApplied = useMemo(() => {
    const hasIdList = !!(memoRequest.idList && memoRequest.idList.trim());
    const hasDateRange = !!(
      memoRequest.dateRange &&
      Array.isArray(memoRequest.dateRange) &&
      memoRequest.dateRange.length > 0
    );
    const hasJob = !!(memoRequest.eventId && memoRequest.eventId > 0);
    const hasProjectScope = !!(
      memoRequest.projectPrimaryEventId && memoRequest.projectPrimaryEventId > 0
    );
    const hasDepartment = !!(memoRequest.userDepartmentId && memoRequest.userDepartmentId > 0);
    return hasIdList || hasDateRange || hasJob || hasProjectScope || hasDepartment;
  }, [memoRequest]);

  // Check if we have memoIds to fetch memos
  // A saved report lists its notes in ai_responses_system (memoIds on the response is deprecated),
  // as Build PDF reads them, so viewing a report can show its photos.
  const aiResponsesSystemQuery = useBatchGetAiResponsesSystemQuery({
    aiResponsesId: aiResponse?.id ?? 0,
    enabled: enabled && (aiResponse?.id ?? 0) > 0 && !aiResponse?.memoIds?.trim(),
  });
  const savedReportMemoIds = useMemo(
    () =>
      (aiResponsesSystemQuery.data?.results ?? [])
        .map((row) => row.memoId)
        .filter((id) => id > 0)
        .join(','),
    [aiResponsesSystemQuery.data?.results],
  );

  const hasMemoIds = useMemo(() => {
    if (memoRequest.idList && memoRequest.idList.trim()) return true;
    if (aiResponse?.memoIds && aiResponse.memoIds.trim()) return true;
    return !!savedReportMemoIds;
  }, [memoRequest.idList, aiResponse?.memoIds, savedReportMemoIds]);

  // Check if we're in "view" mode (aiResponse provided) vs "generate" mode (regular filters)
  const isViewMode = useMemo(() => {
    return !!aiResponse; // If aiResponse is provided, we're viewing an existing report
  }, [aiResponse]);

  // Create memo request for fetching all memos with files
  // If idList is provided, use that. Otherwise, if aiResponse has memoIds, use that.
  // Otherwise, use the regular filters
  const reportMemoRequest = useMemo(() => {
    const req = Memo.clone(memoRequest);
    req.withoutLimit = true;
    req.includeVideoFiles = false;
    req.includeFiles = true; // Include files so we can filter photos/PDFs
    req.includeUnsupportedMobileFiles = false;
    req.includeCommentCount = false;
    req.pageNumber = 0;

    // If memoRequest has idList, use it (for viewing existing reports)
    if (memoRequest.idList && memoRequest.idList.trim()) {
      // idList is already set, keep it
    } else if (aiResponse?.memoIds && aiResponse.memoIds.trim()) {
      // Use memoIds from AiResponse to recreate the report
      req.idList = aiResponse.memoIds;
    } else if (aiResponse && savedReportMemoIds) {
      req.idList = savedReportMemoIds;
    }
    // Otherwise use the regular filters from memoRequest

    return req;
  }, [memoRequest, aiResponse, savedReportMemoIds]);

  const memoQuery = useMemoQuery({
    filters: reportMemoRequest,
    enabled: enabled && (hasFilterApplied || (!!aiResponse && hasMemoIds)), // Fetch immediately when dialog opens and filter is applied, or if viewing existing report with memoIds
  });

  const memoReportQuery = useMemoReportQuery({
    req: reportRequest || CreateAiReportReq.create(),
    enabled: !!reportRequest && workflowStep === 'generating', // Only fetch when we have a request and are generating
  });

  const memoClassificationQuery = useMemoClassificationQuery({
    enabled: true,
  });

  // Handle report query completion
  useEffect(() => {
    if (memoReportQuery.isSuccess && reportRequest && workflowStep === 'generating') {
      const shouldRunInBackground = reportRequest.background;

      if (shouldRunInBackground) {
        // Background processing - show message to check reports page
        toast({
          title: 'Report Generation Started',
          description:
            'Your report is being processed in the background. Check the Reports tab for updates.',
          variant: 'default',
        });
      } else {
        // Immediate response - show success and move to generated screen
        toast({
          title: 'Report Generated Successfully',
          description: 'Your report has been generated and is ready to view.',
          variant: 'success',
        });
      }

      setWorkflowStep('generated');
      onSuccess?.();
    } else if (memoReportQuery.isError && workflowStep === 'generating') {
      // Check if error is due to missing content/input
      const errorMessage = memoReportQuery.error?.toString() || '';
      const isMissingContentError =
        errorMessage.includes('prompt_variable_missing') ||
        errorMessage.includes('Missing prompt variables: input');

      toast({
        title: 'Report Generation Failed',
        description: isMissingContentError
          ? 'Reports are only generated when notes include audio or written content. Please select notes with content to generate a report'
          : 'There was an error generating your report. Please try again.',
        variant: 'destructive',
      });
      setWorkflowStep('form');
    }
  }, [
    memoReportQuery.isSuccess,
    memoReportQuery.isError,
    reportRequest,
    workflowStep,
    onSuccess,
    memoReportQuery.error,
  ]);

  // Collect and filter photo/PDF files from all memos
  const allPhotoFiles = useMemo<{
    files: File[];
    memoCount: number;
    fileToMemoMap: Map<number, Memo>;
  }>(() => {
    if (!memoQuery.data?.results) return { files: [], memoCount: 0, fileToMemoMap: new Map() };

    const files: File[] = [];
    const memoIdsWithPhotos = new Set<number>();
    const fileToMemoMap = new Map<number, Memo>();

    memoQuery.data.results.forEach((memo) => {
      if (memo.files?.results) {
        let hasPhotos = false;
        memo.files.results.forEach((file) => {
          // Filter out video and audio, only keep images and PDFs
          if (
            file.mimeType &&
            (file.mimeType.includes('image') || file.mimeType.includes('pdf')) &&
            !file.mimeType.includes('video') &&
            !file.mimeType.includes('audio')
          ) {
            files.push(file);
            hasPhotos = true;
            // Map file ID to its parent memo
            if (file.id) {
              fileToMemoMap.set(file.id, memo);
            }
          }
        });
        if (hasPhotos && memo.id) {
          memoIdsWithPhotos.add(memo.id);
        }
      }
    });
    return { files, memoCount: memoIdsWithPhotos.size, fileToMemoMap };
  }, [memoQuery.data?.results]);

  const handleViewAllPhotos = () => {
    setPhotoFiles(allPhotoFiles.files);
    setFileToMemoMap(allPhotoFiles.fileToMemoMap);
    setShowPhotoGallery(true);
  };

  const downloadZipMutation = useDownloadZipMutation({
    onSuccess: () => {
      toast({
        title: 'Download Started',
        description: 'Your photos are being downloaded as a zip file.',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Download Failed',
        description: 'There was an error downloading the photos. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleDownloadPhotos = () => {
    if (allPhotoFiles.files.length === 0) {
      toast({
        title: 'No Photos',
        description: 'There are no photos to download.',
        variant: 'default',
      });
      return;
    }

    // Extract file IDs from the photo files
    const fileIds = allPhotoFiles.files
      .map((file) => file.id)
      .filter((id): id is number => id !== undefined && id !== 0);

    if (fileIds.length === 0) {
      toast({
        title: 'No Valid Files',
        description: 'No valid file IDs found for download.',
        variant: 'default',
      });
      return;
    }

    // Create FileIdList request
    const req = FileIdList.create({
      fileIds,
      sizeType: 'original', // or 'compressed' if you want compressed versions
    });

    downloadZipMutation.mutate(req);
  };

  const handleSubmit = async (values: ReportFormValues) => {
    setWorkflowStep('generating');
    try {
      // Use already loaded memos (data is fetched when dialog opens)
      const memos = memoQuery.data?.results || [];

      // Compile array of memo IDs
      const memoIds = memos
        .map((memo) => memo.id)
        .filter((id): id is number => id !== undefined && id !== 0);

      // Validate memo count - prevent generation if more than 100
      if (memoIds.length > 100) {
        setWorkflowStep('form');
        toast({
          title: 'Report Too Large',
          description:
            'This report is too large to generate in one go. Please narrow your filters (smaller date range or fewer notes) and try again.',
          variant: 'destructive',
        });
        return;
      }

      // Determine if we should run in background (> 15 notes)
      const shouldRunInBackground = memoIds.length > 15;

      // Convert memo IDs to CSV string
      const memoIdsCsv = memoIds.join(',');

      const { start: periodStart, end: periodEnd } = extractPeriodFromDateRange(
        memoRequest.dateRange,
      );

      const req = CreateAiReportReq.create({
        reportPromptId: 'pmpt_6939c4f7d02481978014988f10b709cf0e2926d05623a266',
        input: values.customQuery || '',
        eventId: memoRequest.eventId || 0,
        memoIds,
        variables: {
          creator: user.id.toString(),
          memo_ids: memoIdsCsv,
          instructions: values.customQuery || '',
        },
        background: shouldRunInBackground,
        createdAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        reportPeriodStart: periodStart,
        reportPeriodEnd: periodEnd,
      });

      // Update request state - this will enable the query and trigger it automatically
      setReportRequest(req);

      // The useEffect will handle the success/error states and toast notifications
    } catch (error) {
      console.error('Error generating report:', error);
      setWorkflowStep('form');
      toast({
        title: 'Report Generation Failed',
        description: 'There was an error generating your report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateNew = () => {
    setWorkflowStep('form');
    form.reset();
  };

  // Generating state - only show when actually generating
  if (workflowStep === 'generating') {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LoadingIcon className="h-12 w-12" />
          <p className="text-muted-foreground text-sm">Generating report...</p>
        </div>
      </div>
    );
  }

  const reportText = memoReportQuery.data?.textReport || aiResponse?.response || '';
  const reportTitle = reportTypes.find((rt) => rt.id === reportType)?.title;
  const copyReport = async () => {
    try {
      await copyReportToClipboard(reportText);
      toast({ title: 'Report copied', description: 'Paste it into an email or a message.' });
    } catch {
      toast({
        title: 'Couldn’t copy the report',
        description: 'Select the text and copy it instead.',
        variant: 'destructive',
      });
    }
  };
  const photoCaption = (photo: File) => {
    const memo = allPhotoFiles.fileToMemoMap.get(photo.id ?? 0);
    if (!memo) return null;
    return [
      memo.createdByName,
      memo.createdDate ? format(parseISO(memo.createdDate), 'MMM d, yyyy h:mm a') : '',
      memo.eventId ? `Job #${memo.eventId}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  };

  // Generated screen on phones: one primary action (Copy report), the photos as one row that opens
  // a full-screen grid, and the report as text on the page.
  if (workflowStep === 'generated' && isMobileDevice) {
    const photoCount = allPhotoFiles.files.length;
    const secondaryActions = [
      !isViewMode && (
        <Button key="new" variant="outline" className="touch:min-h-11" onClick={handleGenerateNew}>
          New report
        </Button>
      ),
      onBuildPdf && (
        <Button key="pdf" variant="outline" className="touch:min-h-11" onClick={onBuildPdf}>
          Build PDF
        </Button>
      ),
      onDownloadPdf && (
        <Button key="download" variant="outline" className="touch:min-h-11" onClick={onDownloadPdf}>
          Download PDF
        </Button>
      ),
    ].filter(Boolean);
    return (
      <div className="flex flex-col gap-4">
        {onClose && (
          <Button variant="ghost" className="-ml-2 w-fit gap-2" onClick={onClose}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {closeLabel}
          </Button>
        )}
        {reportTitle && <h3 className="text-base font-semibold">{reportTitle}</h3>}
        {reportRequest?.background ? (
          <p className="text-muted-foreground text-sm">
            Your report is being written. Find it in My Reports once it&apos;s ready.
          </p>
        ) : (
          <Button className="w-full" disabled={!reportText} onClick={() => void copyReport()}>
            Copy report
          </Button>
        )}
        {secondaryActions.length > 0 && (
          <div className={`grid gap-2 ${secondaryActions.length > 1 ? 'grid-cols-2' : ''}`}>
            {secondaryActions}
          </div>
        )}
        {photoCount > 0 && (
          <ul aria-label="Photos" className="border-border -mx-4 border-y">
            <ListRow
              title={`Photos · ${photoCount}`}
              details={`From ${allPhotoFiles.memoCount} note${allPhotoFiles.memoCount === 1 ? '' : 's'}`}
              actionLabel="View"
              onSelect={() => setIsPhotoGridOpen(true)}
            />
          </ul>
        )}
        {!reportRequest?.background &&
          (reportText ? (
            <p className="whitespace-pre-wrap break-words text-sm">{reportText}</p>
          ) : (
            <p className="text-muted-foreground text-sm">Loading report…</p>
          ))}

        {isPhotoGridOpen && (
          <Dialog open onOpenChange={(open) => !open && setIsPhotoGridOpen(false)}>
            <DialogContent
              showClose={false}
              // Full screen: nothing to type, so it must not re-center itself for the keyboard.
              keepFocusVisibleAboveKeyboard={false}
              className="bg-background left-0 top-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-3 overflow-y-auto rounded-none border-0 p-4 sm:rounded-none"
            >
              <div className="flex items-center justify-between gap-2">
                <DialogTitle className="text-lg">Photos · {photoCount}</DialogTitle>
                <Button variant="ghost" onClick={() => setIsPhotoGridOpen(false)}>
                  Close
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                {allPhotoFiles.files.map((photo, index) => (
                  <PhotoThumbnail
                    key={photo.id}
                    file={photo}
                    onOpen={() => setOpenPhotoIndex(index)}
                  />
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
        {openPhotoIndex !== null && (
          <PhotoViewer
            photos={allPhotoFiles.files}
            index={openPhotoIndex}
            onIndexChange={setOpenPhotoIndex}
            onClose={() => setOpenPhotoIndex(null)}
            caption={photoCaption}
          />
        )}
      </div>
    );
  }

  // Generated screen
  if (workflowStep === 'generated') {
    return (
      <>
        <div className="space-y-6">
          {/* Top Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {reportTypes.find((rt) => rt.id === reportType) && (
                <Button variant="outline" size="sm" disabled>
                  {reportTypes.find((rt) => rt.id === reportType)?.title}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Only show "Generate New Report" if not in view mode (no idList) */}
              {!isViewMode && (
                <Button variant="outline" size="sm" onClick={handleGenerateNew}>
                  Generate New Report
                </Button>
              )}
              {onClose && (
                <Button variant="outline" size="sm" onClick={onClose}>
                  {closeLabel}
                </Button>
              )}
            </div>
          </div>

          {/* Photo Documentation Card - Show if we have photos from the memo query */}
          {allPhotoFiles.files.length > 0 && (
            <Card className="border-border border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-accent flex h-10 w-10 items-center justify-center rounded-lg">
                      <ImageIcon className="text-foreground h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-foreground font-semibold">Photo Documentation</h3>
                      <p className="text-muted-foreground text-sm">
                        {allPhotoFiles.files.length} photo
                        {allPhotoFiles.files.length === 1 ? '' : 's'} from {allPhotoFiles.memoCount}{' '}
                        note{allPhotoFiles.memoCount === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleViewAllPhotos}
                      className="gap-2"
                    >
                      <EyeOpenIcon className="h-4 w-4" />
                      View All Photos
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPhotos}
                      disabled={downloadZipMutation.isPending || allPhotoFiles.files.length === 0}
                      isLoading={downloadZipMutation.isPending}
                      className="gap-2"
                    >
                      <DownloadIcon className="h-4 w-4" />
                      Download All Photos
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Report Section */}
          {reportRequest?.background ? (
            <div className="border-border bg-muted rounded-lg border p-8 text-center">
              <p className="text-foreground text-sm">
                Please check the My Reports Tab for your report once it&apos;s ready.
              </p>
            </div>
          ) : (
            <Card className="border-border border">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Report</h3>
                  {(memoReportQuery.data?.textReport || aiResponse?.response) && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void copyReport()}
                        className="gap-2"
                      >
                        <CopyIcon className="h-4 w-4" aria-hidden />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const reportText =
                            memoReportQuery.data?.textReport || aiResponse?.response || '';
                          const blob = new Blob([reportText], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `report_${aiResponse?.id || Date.now()}.txt`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                        className="gap-2"
                      >
                        <DownloadIcon className="h-4 w-4" />
                        Download as .txt
                      </Button>
                    </div>
                  )}
                </div>
                {memoReportQuery.data?.textReport || aiResponse?.response ? (
                  <div className="border-border bg-muted rounded-lg border p-4">
                    <pre className="text-foreground whitespace-pre-wrap font-sans text-sm">
                      {memoReportQuery.data?.textReport || aiResponse?.response}
                    </pre>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-muted-foreground text-sm">Loading report...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Photo Gallery Dialog */}
        <Dialog open={showPhotoGallery} onOpenChange={setShowPhotoGallery}>
          <DialogContent className="flex max-h-[90vh] max-w-screen-lg flex-col gap-2 p-2 pt-10 sm:gap-4 sm:p-4 sm:pt-4">
            <DialogHeader className="mr-10">
              <DialogTitle>
                View All Photos ({photoFiles.length} {photoFiles.length === 1 ? 'photo' : 'photos'})
              </DialogTitle>
            </DialogHeader>
            <div className="flex h-[calc(90vh-120px)] w-full">
              {photoFiles.length > 0 && (
                <OmniGallery
                  files={photoFiles}
                  title="All Photos from Filtered Notes"
                  showVideos={false}
                  readOnly={true}
                  renderPhotoHeader={(file) => {
                    const memo = fileToMemoMap.get(file.id ?? 0);
                    if (!memo) return null;
                    return (
                      <div className="bg-muted px-4 py-3">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                          <div>
                            <span className="text-muted-foreground font-semibold">Job Number:</span>{' '}
                            <span className="text-foreground">{memo.eventId || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground font-semibold">Submitted:</span>{' '}
                            <span className="text-foreground">
                              {memo.createdDate
                                ? format(parseISO(memo.createdDate), 'MM/dd/yyyy, h:mm a')
                                : 'N/A'}
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-muted-foreground font-semibold">Property:</span>{' '}
                            <span className="text-foreground">
                              {memo.propertyAddressSearch || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground font-semibold">Department:</span>{' '}
                            <span className="text-foreground">
                              {memo.userDepartmentName || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground font-semibold">Submitter:</span>{' '}
                            <span className="text-foreground">{memo.createdByName || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground font-semibold">Category:</span>{' '}
                            <span className="text-foreground">
                              {memoClassificationQuery.data?.results.find(
                                (c) => c.id === memo.classificationId,
                              )?.categoryDescription || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground font-semibold">Assigned:</span>{' '}
                            <span className="text-foreground">
                              {memo.assigneeName || 'Unassigned'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Form screen - Don't show if in view mode (idList provided)
  // In view mode, we should already be in 'generated' state, but add safety check
  if (isViewMode) {
    // If we're in view mode but somehow on form screen, go to generated
    // This shouldn't happen since we start in 'generated' when aiResponse is provided
    return null;
  }

  // Show loading state while fetching data
  if (memoQuery.isFetching && !memoQuery.data) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LoadingIcon className="h-12 w-12" />
          <p className="text-muted-foreground text-sm">Loading notes...</p>
        </div>
      </div>
    );
  }

  // Show error state if fetch failed
  if (memoQuery.isError) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <p className="text-destructive text-sm">Error loading notes. Please try again.</p>
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Photo Documentation Card - Show in form screen (only if we have memoIds) */}
        {hasMemoIds && allPhotoFiles.files.length > 0 && (
          <Card className="border-border border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-accent flex h-10 w-10 items-center justify-center rounded-lg">
                    <ImageIcon className="text-foreground h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold">Photo Documentation</h3>
                    <p className="text-muted-foreground text-sm">
                      {allPhotoFiles.files.length} photo
                      {allPhotoFiles.files.length === 1 ? '' : 's'} from {allPhotoFiles.memoCount}{' '}
                      note{allPhotoFiles.memoCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleViewAllPhotos}
                    className="gap-2"
                  >
                    <EyeOpenIcon className="h-4 w-4" />
                    View All Photos
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPhotos}
                    disabled={downloadZipMutation.isPending || allPhotoFiles.files.length === 0}
                    isLoading={downloadZipMutation.isPending}
                    className="gap-2"
                  >
                    <DownloadIcon className="h-4 w-4" />
                    Download All Photos
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes Summary - Only show if we have memoIds */}
        {hasMemoIds && memoQuery.data && (
          <div className="border-border bg-muted rounded-lg border p-4">
            <p className="text-muted-foreground text-sm">
              <span className="font-semibold">{memoQuery.data.totalCount || 0}</span> note
              {(memoQuery.data.totalCount || 0) === 1 ? '' : 's'} found
              {memoQuery.data.totalCount && memoQuery.data.totalCount > 100 && (
                <span className="text-warning-foreground ml-2">
                  (Report generation limited to 100 notes)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Report type: only the types that work are offered; one type needs no picker. */}
        {availableReportTypes.length === 1 ? (
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">{availableReportTypes[0].title}</h3>
            <p className="text-muted-foreground text-sm">{availableReportTypes[0].description}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Select Report Type</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {availableReportTypes.map((type) => (
                <Card
                  key={type.id}
                  className={`cursor-pointer transition-all ${
                    reportType === type.id ? 'bg-accent ring-ring ring-2' : 'hover:bg-accent'
                  }`}
                  onClick={() => handleReportTypeClick(type.id)}
                >
                  <CardContent className="space-y-1 p-4">
                    <h4 className="font-semibold">{type.title}</h4>
                    <p className="text-muted-foreground text-sm">{type.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {reportType === 'custom' && (
          <FormField
            control={form.control}
            name="customQuery"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Question</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[100px]" />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        {/* Form Actions - Only show when report type is selected */}
        {reportType && (
          <div className="flex items-center justify-end gap-2">
            {onClose && (
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={
                !hasFilterApplied ||
                (reportType === 'custom' && !form.watch('customQuery')?.trim()) ||
                reportTypes.find((rt) => rt.id === reportType)?.comingSoon
              }
            >
              Generate Report
            </Button>
          </div>
        )}
      </form>

      {/* Photo Gallery Dialog */}
      <Dialog open={showPhotoGallery} onOpenChange={setShowPhotoGallery}>
        <DialogContent className="flex max-h-[90vh] max-w-screen-lg flex-col gap-2 p-2 pt-10 sm:gap-4 sm:p-4 sm:pt-4">
          <DialogHeader className="mr-10">
            <DialogTitle>
              View All Photos ({photoFiles.length} {photoFiles.length === 1 ? 'photo' : 'photos'})
            </DialogTitle>
          </DialogHeader>
          <div className="flex h-[calc(90vh-120px)] w-full">
            {photoFiles.length > 0 && (
              <OmniGallery
                files={photoFiles}
                title="All Photos from Filtered Notes"
                showVideos={false}
                readOnly={true}
                renderPhotoHeader={(file) => {
                  const memo = fileToMemoMap.get(file.id ?? 0);
                  if (!memo) return null;
                  return (
                    <div className="bg-muted px-4 py-3">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div>
                          <span className="text-muted-foreground font-semibold">Job Number:</span>{' '}
                          <span className="text-foreground">{memo.eventId || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-semibold">Submitted:</span>{' '}
                          <span className="text-foreground">
                            {memo.createdDate
                              ? format(parseISO(memo.createdDate), 'MM/dd/yyyy, h:mm a')
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground font-semibold">Property:</span>{' '}
                          <span className="text-foreground">
                            {memo.propertyAddressSearch || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-semibold">Department:</span>{' '}
                          <span className="text-foreground">
                            {memo.userDepartmentName || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-semibold">Submitter:</span>{' '}
                          <span className="text-foreground">{memo.createdByName || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-semibold">Category:</span>{' '}
                          <span className="text-foreground">
                            {memoClassificationQuery.data?.results.find(
                              (c) => c.id === memo.classificationId,
                            )?.categoryDescription || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-semibold">Assigned:</span>{' '}
                          <span className="text-foreground">
                            {memo.assigneeName || 'Unassigned'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
